import {
  bigint,
  boolean,
  decimal,
  double,
  int,
  json,
  mysqlEnum,
  mysqlTable,
  text,
  timestamp,
  tinyint,
  varchar,
} from "drizzle-orm/mysql-core";

export const locationPriceLevelValues = {
  budget: "bis 500",
  moderate: "bis 1.000",
  upscale: "bis 2.500",
  premium: "bis 5.000",
  luxury: "mehr als 5.000",
};

export const location = mysqlTable("lg_location", {
  id: int("uid", { unsigned: true }).primaryKey().autoincrement(),

  // TYPO3 Standard Fields
  pid: int("pid").default(0).notNull(), // Page ID where record is stored
  deleted: tinyint("deleted").default(0).notNull(), // Soft delete
  hidden: tinyint("hidden").default(0).notNull(), // Hide in frontend
  sorting: int("sorting").default(0).notNull(), // Manual sorting

  // NOTE: TYPO3 IRRE parent count columns (tx_irre_*) are DB-only for FormEngine.
  // Do not map them here — `.select().from(location)` would break if migrate lags deploy.

  name: varchar("name", { length: 255 }).notNull(),

  capacitySeated: int("capacity_seated"),
  capacityStanding: int("capacity_standing"),

  priceLevel: mysqlEnum("price_level", [
    "budget",
    "moderate",
    "upscale",
    "premium",
    "luxury",
  ]),

  cateringPartnership: boolean("catering_partnership"),

  internet: boolean("internet"),
  internetSpeed: varchar("internet_speed", { length: 255 }),

  maleToiletCount: int("male_toilet_count"),
  femaleToiletCount: int("female_toilet_count"),
  accessibleToiletCount: int("accessible_toilet_count"),

  coveredParkingSpaceCount: int("covered_parking_space_count"),

  parkingFeePer24h: double("parking_fee_per_24h"),

  insideAreaSum: int("inside_area_sum"),
  biggestRoom: int("biggest_room"),
  outsideLargestArea: int("outside_largest_area"),
  outsideAreaSum: int("outside_area_sum"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().onUpdateNow().notNull(),

  plan: mysqlEnum("plan", ["superior", "premium", "member", "basic"]),
  billingInterval: mysqlEnum("billing_interval", ["half-year", "full-year"]),
  // Set to true once the owner has completed the plan ordering step.
  // This is needed because locations can remain in "draft" state (e.g. profanity/under-review)
  // while the user should not be asked to order the plan again.
  planOrdered: boolean("plan_ordered").default(false).notNull(),
  nameForm: json("name_form").$type<{
    german: string;
    english: string;
  }>(),
  street: varchar("street", { length: 255 }),
  houseNumber: varchar("house_number", { length: 50 }),
  postalCode: varchar("postal_code", { length: 20 }),
  city: varchar("city", { length: 255 }),
  country: varchar("country", { length: 255 }),
  state: varchar("state", { length: 10 }),
  lat: decimal("lat", { precision: 11, scale: 8, mode: "number" }).notNull(),
  lng: decimal("lng", { precision: 12, scale: 8, mode: "number" }).notNull(),
  onlyPredefinedOccasions: boolean("only_predefined_occasions"),

  minGuests: int("min_guests"),
  minBudgetTotal: int("min_budget_total"),
  minBudgetPerPerson: int("min_budget_per_person"),
  currency: mysqlEnum("currency", ["EUR", "CHF", "USD"]),
  markTelephoneRequired: boolean("mark_telephone_required"),
  languages: json("languages").$type<string[]>(),

  description: text("description"),
  descriptionForm: json("description_form").$type<{
    german: string;
    english: string;
  }>(),
  descriptionLong: json("description_long").$type<{
    german: string;
    english: string;
  }>(),

  virtualTour: varchar("virtual_tour", { length: 255 }),

  rentalOutOfHoursPossible: boolean("rental_out_of_hours_possible"),
  additionalRentalInformation: json("additional_rental_information").$type<{
    german: string;
    english: string;
  }>(),

  priceMode: mysqlEnum("price_mode", ["net", "gross"]),
  withVat: boolean("with_vat"),
  vatAmount: int("vat_amount"),
  priceIsPublic: boolean("price_is_public"),
  rateDaily: int("rate_daily"),
  rateDailyHourLimit: int("rate_daily_hour_limit"),
  rateHourly: int("rate_hourly"),
  rateHourlyMinimumHours: int("rate_hourly_minimum_hours"),
  minimumConsumptionPrice: int("minimum_consumption_price"),
  minimumConsumptionPersons: int("minimum_consumption_persons"),
  consumptionFlatRatePerPerson: int("consumption_flat_rate_per_person"),
  consumptionFlatRateMinimumPersons: int(
    "consumption_flat_rate_minimum_persons"
  ),
  additionalPricingInformation: json("additional_pricing_information").$type<{
    german: string;
    english: string;
  }>(),
  cleaningCosts: int("cleaning_costs"),
  additionalCleaningCostsInformation: json(
    "additional_cleaning_costs_information"
  ).$type<{
    german: string;
    english: string;
  }>(),
  additionalCosts: int("additional_costs"),
  additionalCostsInformation: json(
    "additional_costs_information"
  ).$type<{
    german: string;
    english: string;
  }>(),
  openingYear: int("opening_year"),
  yearOfRenovation: int("year_of_renovation"),
  areaType: mysqlEnum("area_type", ["indoor", "outdoor", "mixed"]),
  locationShortDescription: json("location_short_description").$type<{
    german: string;
    english: string;
  }>(),

  maxLength: bigint("max_length", { mode: "number" }),
  maxWidth: bigint("max_width", { mode: "number" }),
  maxHeight: bigint("max_height", { mode: "number" }),
  maxArea: bigint("max_area", { mode: "number" }),
  indoorAreaTotal: bigint("indoor_area_total", { mode: "number" }),
  seatingBanquet: int("seating_banquet"),
  seatingBlock: int("seating_block"),
  seatingParliamentary: int("seating_parliamentary"),
  seatingRow: int("seating_row"),
  seatingTable: int("seating_table"),
  seatingUShape: int("seating_ushape"),
  seatingParty: int("seating_party"),
  seatingStanding: int("seating_standing"),
  maxCapacity: int("max_capacity"),
  minimumCapacity: int("minimum_capacity"),
  stage: boolean("stage"),
  stageLength: bigint("stage_length", { mode: "number" }),
  stageWidth: bigint("stage_width", { mode: "number" }),
  stageDepth: bigint("stage_depth", { mode: "number" }),
  stageHeight: bigint("stage_height", { mode: "number" }),
  stageArea: bigint("stage_area", { mode: "number" }),
  stageFixed: boolean("stage_fixed"),
  additionalStageInformation: json("additional_stage_information").$type<{
    german: string;
    english: string;
  }>(),
  catering: mysqlEnum("catering", ["free", "permanentPartner", "freeChoicePaid", "multiplePartner"]),
  tech: mysqlEnum("tech", ["free", "permanentPartner", "freeChoicePaid", "multiplePartner"]),
  contractDetails: json("contract_details").$type<{
    german: string;
    english: string;
  }>(),
  staff: boolean("staff"),
  kitchen: boolean("kitchen"),
  cateringRoom: boolean("catering_room"),
  tableware: boolean("tableware"),
  waterSupply: boolean("water_supply"),
  v230: boolean("v230"),
  v110: boolean("v110"),
  a16: boolean("a16"),
  a32: boolean("a32"),
  a63: boolean("a63"),
  a128: boolean("a128"),
  lighting: boolean("lighting"),
  presentationAndVideo: boolean("presentation_and_video"),
  sound: boolean("sound"),
  heatingAndAirConditioning: boolean("heating_and_air_conditioning"),
  rigging: boolean("rigging"),
  connection5G: boolean("connection_5g"),
  connection4G: boolean("connection_4g"),
  connection3G: boolean("connection_3g"),
  connection2G: boolean("connection_2g"),
  bandWidth: int("band_width"),
  furnished: boolean("furnished"),
  furnishedForm: mysqlEnum("furnished_form", ["yes", "no", "bookable"]),
  amountOfChairs: int("amount_of_chairs"),
  amountOfTables: int("amount_of_tables"),
  additionalFurnitureInformation: json(
    "additional_furniture_information"
  ).$type<{
    german: string;
    english: string;
  }>(),
  wcFemale: boolean("wc_female"),
  wcMale: boolean("wc_male"),
  wcAccessible: boolean("wc_accessible"),
  smokingArea: boolean("smoking_area"),
  artistDressingRooms: boolean("artist_dressing_rooms"),
  noNoiseControlRequirements: boolean("no_noise_control_requirements"),
  showers: boolean("showers"),
  backstageArea: boolean("backstage_area"),
  danceFloor: boolean("dance_floor"),
  accessibleForTrucks: boolean("accessible_for_trucks"),
  accessibleForCars: boolean("accessible_for_cars"),
  freightElevator: boolean("freight_elevator"),
  loadingDock: boolean("loading_dock"),
  additionalLoadingDockInformation: json(
    "additional_loading_dock_information"
  ).$type<{
    german: string;
    english: string;
  }>(),
  parkingLots: mysqlEnum("parking_lots", ["yes", "no", "noOwn"]),
  parkingLotRoof: boolean("parking_lot_roof"),
  parkingSpaceCount: int("parking_space_count"),
  additionalParkingSpaceInformation: json(
    "additional_parking_space_information"
  ).$type<{
    german: string;
    english: string;
  }>(),
  accommodations: boolean("accommodations"),
  hotelRooms: int("hotel_rooms"),
  hotelSingleRooms: int("hotel_single_rooms"),
  hotelDoubleRooms: int("hotel_double_rooms"),
  hotelDorms: int("hotel_dorms"),
  freeWifi: boolean("free_wifi"),
  freeLan: boolean("free_lan"),
  hotelStars: int("hotel_stars"),
  hotelDescription: json("hotel_description").$type<{
    german: string;
    english: string;
  }>(),
  reception24hours: boolean("reception_24hours"),
  wakeUpService: boolean("wake_up_service"),
  roomService: boolean("room_service"),
  conferenceOffice: boolean("conference_office"),
  hotelSafe: boolean("hotel_safe"),
  // used for hotel section
  wifi: boolean("wifi"),
  lan: boolean("lan"),

  roomsWithShowers: boolean("rooms_with_showers"),
  roomsWithBath: boolean("rooms_with_bath"),
  roomsWithAirConditioning: boolean("rooms_with_air_conditioning"),
  sightseeing: boolean("sightseeing"),
  golf: boolean("golf"),
  sailing: boolean("sailing"),
  tennis: boolean("tennis"),
  ski: boolean("ski"),
  spa: boolean("spa"),
  swimmingIndoor: boolean("swimming_indoor"),
  massage: boolean("massage"),
  swimmingOutdoor: boolean("swimming_outdoor"),
  additionalLeisureInformation: json("additional_leisure_information").$type<{
    german: string;
    english: string;
  }>(),
  contactPersonFirstName: varchar("contact_person_first_name", {
    length: 255,
  }),
  contactPersonLastName: varchar("contact_person_last_name", { length: 255 }),
  contactPersonPosition: varchar("contact_person_position", { length: 255 }),
  contactPersonEmail: varchar("contact_person_email", { length: 255 }),
  contactPersonPhone: varchar("contact_person_phone", { length: 255 }),
  contactEmail: varchar("contact_email", { length: 255 }),
  ccEmails: json("cc_emails").$type<string[]>(),
  contactTelephone: varchar("contact_telephone", { length: 255 }),
  website: varchar("website", { length: 255 }),
  websites: json("websites").$type<{ label: string; url: string }[]>(),
  socialLinkedin: varchar("social_linkedin", { length: 255 }),
  socialInstagram: varchar("social_instagram", { length: 255 }),
  socialProfiles: json("social_profiles").$type<{ label: string; url: string }[]>(),
  imprintCompany: varchar("imprint_company", { length: 255 }),
  imprintFirstName: varchar("imprint_first_name", { length: 255 }),
  imprintLastName: varchar("imprint_last_name", { length: 255 }),
  imprintEmail: varchar("imprint_email", { length: 255 }),
  imprintStreet: varchar("imprint_street", { length: 255 }),
  imprintHouseNumber: varchar("imprint_house_number", { length: 50 }),
  imprintZipCode: varchar("imprint_zip_code", { length: 20 }),
  imprintCity: varchar("imprint_city", { length: 255 }),
  imprintCountry: varchar("imprint_country", { length: 255 }),

  faq: json("faq").$type<
    Array<{
      question: { german: string; english: string };
      answer: { german: string; english: string };
    }>
  >(),

  status: mysqlEnum("status", ["draft", "published"]),

  highlightLabel: varchar("highlight_label", { length: 20 }),

  additionalTechnologyInformation: json(
    "additional_technology_information",
  ).$type<{
    german: string;
    english: string;
  }>(),
  additionalSanitaryInformation: json(
    "additional_sanitary_information",
  ).$type<{
    german: string;
    english: string;
  }>(),
  accessible: boolean("accessible"),
  blackoutCapable: boolean("blackout_capable"),
  daylight: boolean("daylight"),
  sauna: boolean("sauna"),
});
